"use client";

import * as React from "react";

export type FitCountOptions = {
  /** Natural width of each item, in order. */
  widths: readonly number[];
  /** Width the items may fill. */
  capacity: number;
  /** Gap between adjacent items, in px. */
  gap: number;
  /** Width held for the overflow control while `hidden` items remain, gap
   *  included. Zero once nothing is hidden. */
  reserve?: (hidden: number) => number;
  /** In-flow width no fold can reclaim (a separator, a pinned control). */
  fixed?: number;
  /**
   * Hysteresis: the count from the last pass. A visible item may squash to
   * `minItemWidth` before it folds, so a transient squeeze (hover actions
   * claiming their slot, a small drag) truncates in place instead of
   * popping items into the overflow. Both must be given together.
   */
  previous?: number;
  minItemWidth?: number;
  /** The fewest items to keep; one clipped item beats a bare row. */
  min?: number;
};

/**
 * How many items of a one-line row fit, greedily from the first. An item
 * earns its slot only if the overflow control for whatever remains after it
 * still fits too: the control must never be the thing that overflows.
 */
export function computeFitCount({
  widths,
  capacity,
  gap,
  reserve = () => 0,
  fixed = 0,
  previous,
  minItemWidth,
  min = 0,
}: FitCountOptions): number {
  let used = fixed;
  let count = 0;
  for (let i = 0; i < widths.length; i += 1) {
    const width = (widths[i] ?? 0) + (count > 0 ? gap : 0);
    const hiddenAfter = widths.length - (i + 1);
    const held = hiddenAfter > 0 ? reserve(hiddenAfter) : 0;
    if (used + width + held > capacity) break;
    used += width;
    count += 1;
  }
  if (previous !== undefined && minItemWidth !== undefined) {
    const prev = Math.min(previous, widths.length);
    if (prev > count) {
      let squashed = fixed;
      for (let i = 0; i < prev; i += 1) {
        squashed += Math.min(widths[i] ?? 0, minItemWidth) + (i > 0 ? gap : 0);
      }
      const held = prev < widths.length ? reserve(widths.length - prev) : 0;
      if (squashed + held <= capacity) count = prev;
    }
  }
  return Math.max(min, count);
}

export type UseFitCountOptions = Omit<FitCountOptions, "widths" | "capacity" | "previous"> & {
  /** Reads the items' natural widths; called on every measure. */
  measure: () => { widths: number[]; capacity: number } | null;
  /** A changed item set is a NEW layout, not a transient squeeze: the
   *  hysteresis memory resets when this changes. */
  signature: string;
};

/**
 * `computeFitCount` bound to a container: measures before paint whenever the
 * item set changes and again on every container resize. The count cannot
 * loop the observer — the container's width is parent-driven, unchanged by
 * how many items render.
 */
export function useFitCount(
  container: React.RefObject<HTMLElement | null>,
  { measure, signature, ...options }: UseFitCountOptions,
): number {
  const [count, setCount] = React.useState(Number.MAX_SAFE_INTEGER);
  const countRef = React.useRef(0);
  const optionsRef = React.useRef({ measure, ...options });
  optionsRef.current = { measure, ...options };

  const recompute = React.useCallback(() => {
    const { measure: read, ...rest } = optionsRef.current;
    const measured = read();
    if (!measured) return;
    const next = computeFitCount({
      ...rest,
      widths: measured.widths,
      capacity: measured.capacity,
      previous: countRef.current,
    });
    countRef.current = next;
    setCount(next);
  }, []);

  React.useLayoutEffect(() => {
    countRef.current = 0;
    recompute();
    const node = container.current;
    if (!node || typeof ResizeObserver === "undefined") return undefined;
    const observer = new ResizeObserver(recompute);
    observer.observe(node);
    return () => observer.disconnect();
    // `signature` stands in for the item set.
  }, [container, recompute, signature]);

  return count;
}
